import React, { useState, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { Link } from "react-router-dom";
import "../assets/css/ViewCustomer.css";
import axiosInstance from "../components/api/axiosInstance";
import SocialPasswordReveal from "../components/SocialPasswordReveal";
import SocialSignInButton from "../components/SocialSignInButton";
import { LOGIN_OWN, socialLoginUrl, socialPlatformLabel } from "../constants/socialPlatforms";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : null);

const twoFaLabel = (value) => (value === true ? "On" : value === false ? "Off" : null);

function InfoItem({ label, value, children }) {
  const hasValue = children != null || (value && value !== "—" && value !== "");
  return (
    <div className="viewlead-info-item">
      <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
      <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>
        {children != null ? children : hasValue ? value : "—"}
      </span>
    </div>
  );
}

function LinkValue({ url }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#2977ed", textDecoration: "none" }}>
      {url}
    </a>
  );
}

function ViewAccount({ accountId, onBack, onEditAccount }) {
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAccount = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/social-account/${accountId}`);
        setAccountData(response.data?.Account_Id ? response.data : response.data?.data || null);
      } catch (error) {
        console.error("Error fetching account:", error);
        setError(error.response?.data?.message || "Failed to load account details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (accountId) {
      fetchAccount();
    }
  }, [accountId]);

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading account details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="viewlead-container">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Accounts
        </button>
        <div className="error-message" style={{ 
          backgroundColor: '#fee', 
          color: '#c33', 
          padding: '12px', 
          borderRadius: '4px', 
          marginBottom: '20px',
          fontSize: '14px'
        }}>{error}</div>
      </div>
    );
  }

  if (!accountData) {
    return (
      <div className="viewlead-container">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Accounts
        </button>
        <div className="viewlead-loading">Account not found.</div>
      </div>
    );
  }

  const account = accountData;
  const ownLogin = (account.Login_Method || LOGIN_OWN) === LOGIN_OWN;
  const linked = account.Linked_Account;
  const grid3 = { gridTemplateColumns: "repeat(3, 1fr)" };

  return (
    <div className="viewlead-container">
      {/* Header */}
      <div className="viewlead-header">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Accounts
        </button>
        <button className="btn-edit-lead" onClick={() => onEditAccount && onEditAccount(accountId)}>
          Edit Account
        </button>
      </div>

      <div className="viewlead-card">
        <h3 className="viewlead-section-title">Account</h3>
        <div className="viewlead-info-grid" style={grid3}>
          <InfoItem label="Platform" value={socialPlatformLabel(account.Platform_Name)} />
          <InfoItem label="Account Name" value={account.Account_Name} />
          <InfoItem label="Status" value={account.Status} />
          <InfoItem label="Profile URL">{account.Profile_URL ? <LinkValue url={account.Profile_URL} /> : null}</InfoItem>
          {account.Business_URL ? (
            <InfoItem label={account.Platform_Name === "gmb" ? "Maps Listing URL" : "Business Page URL"}>
              <LinkValue url={account.Business_URL} />
            </InfoItem>
          ) : null}
        </div>

        <h3 className="viewlead-section-title">Login</h3>
        <div className="viewlead-info-grid" style={grid3}>
          <InfoItem label="Login Method" value={account.Login_Method || LOGIN_OWN} />
          {!ownLogin ? (
            <InfoItem label="Signs in with">
              {linked ? (
                <Link
                  to={`/tenants/${account.client_id}/social-accounts/${linked.Account_Id}/view`}
                  style={{ color: "#2977ed", textDecoration: "none" }}
                >
                  {socialPlatformLabel(linked.Platform_Name)} — {linked.Login || linked.Account_Name}
                </Link>
              ) : null}
            </InfoItem>
          ) : null}
          {account.Has_Password ? (
            <InfoItem label="Username & Password">
              <SocialPasswordReveal
                key={account.Updated_On || account.Account_Id}
                hasPassword
                revealUrl={`/social-account/${account.Account_Id}/reveal`}
                pickPassword={(res) => res.data?.Password}
                pickUsername={(res) => res.data?.Username}
              />
            </InfoItem>
          ) : (
            <InfoItem label={ownLogin ? "Username" : "Handle"} value={account.Username} />
          )}
          <InfoItem label="Email Used" value={account.Email_Used} />
          {account.Has_Password ? (
            <InfoItem label="Password Last Changed" value={formatDate(account.Password_Updated_On)} />
          ) : null}
          {socialLoginUrl(account.Platform_Name) ? (
            <InfoItem label="Sign In">
              <SocialSignInButton platform={account.Platform_Name} username={ownLogin ? account.Username : null} />
            </InfoItem>
          ) : null}
        </div>

        <h3 className="viewlead-section-title">Security &amp; Recovery</h3>
        <div className="viewlead-info-grid" style={grid3}>
          <InfoItem label="2-Step Verification" value={twoFaLabel(account.Two_FA_Enabled)} />
          {account.Two_FA_Enabled ? (
            <>
              <InfoItem label="Code Sent By" value={account.Two_FA_Method} />
              <InfoItem label="Code Received By" value={account.Two_FA_Holder} />
            </>
          ) : null}
          <InfoItem label="Recovery Email" value={account.Recovery_Email} />
          <InfoItem label="Recovery Phone" value={account.Contact_Number} />
        </div>

        <h3 className="viewlead-section-title">Ownership</h3>
        <div className="viewlead-info-grid" style={grid3}>
          <InfoItem label="Access Type" value={account.Access_Type} />
          <InfoItem
            label="Account Owner"
            value={account.Account_Owner === "Agency" ? "Agency (created by us)" : account.Account_Owner}
          />
          <InfoItem label="Verification" value={account.Verification_Status} />
        </div>

        <h3 className="viewlead-section-title">Notes</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Notes" value={account.Notes} />
        </div>
      </div>
    </div>
  );
}

export default ViewAccount;
